/**
 * Communications data layer — the ONLY place the app gets or sends customer messages.
 *
 * The Communications page talks to this module and nothing else:
 *
 *   live   texts are real (Twilio) — on when REACT_APP_TEXTS_URL is set and staff sign in with Supabase
 *   mock   test conversations in memory (local test mode / tests)
 *
 * ── Message (every provider returns this shape) ─────────────────────────────────────────────
 *   id          string   our id
 *   leadId      string   the conversation key: the customer's phone (E.164) when live, the lead id in test mode
 *   channel     'sms' | 'email' | 'call'
 *   direction   'in' (from the customer) | 'out' (from CIE)
 *   at          ISO timestamp
 *   body        text (sms / email)
 *   subject     email only
 *   durationSec call only — 0 = missed / no answer
 *   status      'sending' | 'sent' | 'delivered' | 'failed' | 'undelivered' | 'received'
 *   sentBy      who sent it ('Admin', a staff name, 'Automatic' for booking texts / reminders)
 *   provider    'twilio' | 'zoho-mail' | 'mock'
 *   externalId  the provider's own id (Twilio MessageSid)
 *
 * ── Live texts ──────────────────────────────────────────────────────────────────────────────
 * Every text lives in Supabase `comms_messages` (shared by all staff, one row per message). The app
 * only reads it; n8n writes it:
 *   send      app → "[C] App 7 - Texts" (POST REACT_APP_TEXTS_URL) → Twilio from (249) 700-4555,
 *             stored + logged as a Note on the customer's Deal in Zoho
 *   incoming  Twilio → "[C] Twilio 1 - Incoming Texts" → matched to the Zoho Inspection by phone
 *             (unmatched numbers are kept as "Not in Zoho"), stored + Note in Zoho
 *   status    Twilio → "[C] Twilio 2 - Text Status" → delivered / failed; booking confirmations and
 *             reminders sent by the booking workflows join the customer's conversation this way too
 * Twilio keys live only in n8n — never in this app.
 *
 * ── Live email (Zoho Mail, support@canadianinsulationexperts.com) ──────────────────────────
 * Same table, channel 'email', filed under the same customer key as their texts (phone number):
 *   send      app → "[C] App 10 - Email" (POST REACT_APP_EMAIL_URL) → Zoho Mail API as support@,
 *             stored + Note on the Deal
 *   incoming  "[C] Email 1 - Mailbox" checks Zoho Mail every minute: emails to support@ from a customer
 *             whose email is on a Zoho Inspection (others stay in Zoho Mail only), plus emails staff send
 *             as support@ from Zoho Mail itself. Quoted earlier messages are trimmed off.
 */

import { getLeads as getAllLeads } from './leads';
import { getFollowUps } from './followUps';
import mockCommunications, { initiallyUnread } from '../mockData/mockCommunications';
import { useSyncExternalStore } from 'react';
import { AUTH_PROVIDER, getAccessToken, getSupabase } from './auth';

const TEXTS_URL = process.env.REACT_APP_TEXTS_URL;
const EMAIL_URL = process.env.REACT_APP_EMAIL_URL;
const PROVIDER = TEXTS_URL && AUTH_PROVIDER === 'supabase' ? 'live' : 'mock';
const POLL_MS = 10000;

/* ---------- helpers both providers use ---------- */

// Twilio needs E.164: (416) 555-1234 → +14165551234
export const toE164 = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return '';
};

export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || ''));

const lastOf = (msgs) => msgs[msgs.length - 1];

/* ---------- mock provider: test conversations, behaves like the real thing ---------- */

const mockStore = {
  threads: Object.fromEntries(
    Object.entries(mockCommunications).map(([leadId, msgs]) => [
      leadId,
      msgs.map((m) => ({
        ...m,
        leadId,
        provider: 'mock',
        status: m.direction === 'in' ? 'received' : 'delivered',
      })),
    ])
  ),
  unread: new Set(initiallyUnread),
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const mockProvider = {
  async listConversations() {
    await wait(150);
    return Object.entries(mockStore.threads)
      .filter(([, msgs]) => msgs.length)
      .map(([leadId, msgs]) => ({
        leadId,
        lastMessage: lastOf(msgs),
        unreadCount: mockStore.unread.has(leadId) ? 1 : 0,
        channels: [...new Set(msgs.map((m) => m.channel))],
      }));
  },

  async getThread(leadId) {
    await wait(150);
    return [...(mockStore.threads[leadId] || [])];
  },

  // fails the same way the real send would when there's nothing valid to send to
  async send(leadId, { channel, to, body, subject }) {
    await wait(500);
    if (channel === 'sms' && !toE164(to)) throw new Error('No valid mobile number for this customer');
    if (channel === 'email' && !isEmail(to)) throw new Error('No email address for this customer');
    const msg = {
      id: `mock-${Date.now()}`,
      leadId,
      channel,
      direction: 'out',
      at: new Date().toISOString(),
      body,
      ...(channel === 'email' ? { subject } : {}),
      status: 'sent',
      provider: 'mock',
      externalId: null,
    };
    mockStore.threads[leadId] = [...(mockStore.threads[leadId] || []), msg];
    return msg;
  },

  async markRead(leadId) {
    mockStore.unread.delete(leadId);
  },

  async changesSince() {
    return []; // nothing arrives on its own in test mode
  },
};

/* ---------- live provider: Supabase (read) + n8n → Twilio (send) ---------- */

const AUTOMATIC = { automated: 'Automatic', import: 'Automatic' };

const fromRow = (r) => ({
  id: r.id,
  leadId: r.contact,
  channel: r.channel,
  direction: r.direction,
  at: r.at,
  updatedAt: r.updated_at,
  body: r.body,
  subject: r.subject || undefined,
  address: r.address || undefined,
  status: r.status,
  error: r.error || undefined,
  sentBy: r.direction === 'out' ? r.sent_by_name || AUTOMATIC[r.source] || '' : '',
  durationSec: r.duration_sec == null ? undefined : r.duration_sec,
  provider: r.provider,
  externalId: r.external_id,
});

const db = () => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Sign in again to load messages.');
  return supabase;
};

const check = ({ data, error }) => {
  if (error) throw new Error(error.message || 'Couldn’t load messages');
  return data;
};

const liveProvider = {
  async listConversations() {
    const rows = check(await db().rpc('comms_conversations'));
    return (rows || []).map((c) => ({
      leadId: c.contact,
      lastMessage: fromRow(c.last_message),
      unreadCount: c.unread_count,
      channels: c.channels || ['sms'],
      email: c.email || '', // last email address used with this customer
      // who Zoho says this number belongs to (null = not in Zoho)
      customer: c.customer_name ? { name: c.customer_name, zohoModule: c.zoho_module, zohoId: c.zoho_id, dealId: c.deal_id } : null,
    }));
  },

  async getThread(key) {
    const rows = check(await db().from('comms_messages').select('*').eq('contact', key).order('at', { ascending: true }).limit(500));
    return rows.map(fromRow);
  },

  async send(key, { channel, to, body, subject, replyTo }) {
    const email = channel === 'email';
    if (email && !EMAIL_URL) throw new Error('Email isn’t connected yet — send a text');
    if (email && !isEmail(to)) throw new Error('No email address for this customer');
    if (!email && !toE164(to)) throw new Error('No valid mobile number for this customer');
    const token = await getAccessToken();
    if (!token) throw new Error(`Sign in again to send ${email ? 'emails' : 'texts'}`);
    let res;
    try {
      res = await fetch(email ? EMAIL_URL : TEXTS_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(
          email ? { action: 'send', contact: key, to, subject, body, replyTo } : { action: 'send', to: toE164(to), body }
        ),
      });
    } catch (e) {
      throw new Error('No connection — not sent');
    }
    const out = await res.json().catch(() => ({}));
    if (!res.ok || !out.ok) throw new Error(out.error || `Not sent (${res.status})`);
    return fromRow(out.message);
  },

  async markRead(key) {
    check(await db().rpc('comms_mark_read', { p_contact: key }));
    refreshUnread();
  },

  async changesSince(since) {
    const rows = check(
      await db().from('comms_messages').select('*').gt('updated_at', since).order('updated_at', { ascending: true }).limit(200)
    );
    return rows.map(fromRow);
  },
};

const provider = PROVIDER === 'live' ? liveProvider : mockProvider;

/* ---------- what the page uses ---------- */

export const PROVIDER_NAME = PROVIDER;
export const LIVE = PROVIDER === 'live';
export const EMAIL_CONNECTED = !LIVE || !!EMAIL_URL; // live: Zoho Mail via n8n · test mode keeps the email UI working

// the conversation a lead belongs to: live = their phone number (texts, calls and emails together;
// email address only when there's no phone), test data per lead
export const conversationKey = (lead) =>
  (LIVE
    ? toE164(lead && lead.servicePhone) || (isEmail(lead && lead.email) ? lead.email.trim().toLowerCase() : '')
    : lead && lead.id) || null;

export const listConversations = () => provider.listConversations();
export const getThread = (leadId) => provider.getThread(leadId);
export const markRead = (leadId) => provider.markRead(leadId);

// who a message goes to — the lead's own contact details, normalised for the provider
export const recipientFor = (lead, channel) =>
  channel === 'sms' ? toE164(lead && lead.servicePhone) : (lead && lead.email) || '';

export const sendMessage = (leadId, payload) => provider.send(leadId, payload);

/**
 * New / updated messages while the page is open. Polls for now; a push channel
 * (e.g. Supabase realtime or server-sent events) can replace this without touching the page.
 * Returns an unsubscribe function.
 */
export const subscribe = (onMessages) => {
  let since = new Date(Date.now() - 60000).toISOString();
  let stopped = false;
  const tick = async () => {
    if (stopped) return;
    try {
      const changed = await provider.changesSince(since);
      if (changed && changed.length) {
        since = changed.reduce((max, m) => ((m.updatedAt || m.at) > max ? m.updatedAt || m.at : max), since);
        onMessages(changed);
      }
    } catch (err) {
      console.warn('Communications: could not check for new messages', err);
    }
  };
  const timer = setInterval(tick, POLL_MS);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
};

// leads come from the same place as the rest of the app
// one customer list: route leads + follow-ups (a follow-up that came from a lead uses the lead's id,
// so its texts and the lead's texts are the same thread). Live: the follow-ups list is still test data
// (the real ones are the Calls board), so it's left out — everyone else is named from Zoho.
export const getLeads = () => {
  const leads = getAllLeads();
  if (LIVE) return leads;
  const known = new Set(leads.map((l) => l.id));
  const fromFollowUps = getFollowUps()
    .filter((f) => !(f.leadId && known.has(f.leadId)))
    .map((f) => ({
      id: f.leadId || f.id,
      contactName: f.name,
      servicePhone: f.phone,
      email: f.email || '',
      serviceAddress: f.address,
      stage: 'Follow-up',
    }));
  return [...leads, ...fromFollowUps];
};

/* ---------- unread texts, for the menu badge ---------- */
let unread = 0;
const unreadListeners = new Set();
export const refreshUnread = async () => {
  if (!LIVE) return;
  try {
    const list = await liveProvider.listConversations();
    const next = list.filter((c) => c.unreadCount > 0).length;
    if (next !== unread) {
      unread = next;
      unreadListeners.forEach((fn) => fn());
    }
  } catch (e) {
    // signed out / offline — keep the last count
  }
};
let unreadTimer = null;
const subscribeUnread = (fn) => {
  unreadListeners.add(fn);
  if (LIVE && !unreadTimer) {
    refreshUnread();
    unreadTimer = setInterval(refreshUnread, 30000);
  }
  return () => {
    unreadListeners.delete(fn);
    if (!unreadListeners.size && unreadTimer) {
      clearInterval(unreadTimer);
      unreadTimer = null;
    }
  };
};
// customers waiting on a reply (conversations with unread texts)
export const useUnreadTexts = () => useSyncExternalStore(subscribeUnread, () => unread);
